/**
 * localStorage-backed bookmark store for unauthenticated visitors.
 *
 * Keeps minimal metadata per bookmark so the UI can render without a network
 * round-trip. When the visitor logs in, UserProvider reads these items, pushes
 * them to the server via `POST /v2/users/me/bookmarks/bulk`, and clears local.
 *
 * Storage key: `be.guestBookmarks.v1`
 * Cap: 200 entries (oldest dropped).
 * Multi-tab: writes dispatch a `be.bookmarks.changed` window event.
 */

const STORAGE_KEY = 'be.guestBookmarks.v1';
const CHANGE_EVENT = 'be.bookmarks.changed';
const MAX_ITEMS = 200;

export interface GuestBookmarkItem {
    id: string;
    slug?: string;
    title?: string;
    addedAt: number;
}

interface GuestBookmarkStore {
    version: 1;
    items: GuestBookmarkItem[];
}

function emptyStore(): GuestBookmarkStore {
    return {version: 1, items: []};
}

function read(): GuestBookmarkStore {
    if (typeof window === 'undefined') return emptyStore();
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return emptyStore();
        const parsed = JSON.parse(raw) as GuestBookmarkStore;
        if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.items)) {
            return emptyStore();
        }
        return parsed;
    } catch {
        return emptyStore();
    }
}

function write(store: GuestBookmarkStore): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
        window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
    } catch {
        // Quota exceeded or serialization error — best effort.
    }
}

export class GuestBookmarkRepository {
    static load(): GuestBookmarkItem[] {
        return read().items;
    }

    static has(id: string): boolean {
        return read().items.some((i) => i.id === id);
    }

    static add(item: Omit<GuestBookmarkItem, 'addedAt'>): GuestBookmarkItem[] {
        const store = read();
        if (store.items.some((i) => i.id === item.id)) {
            return store.items;
        }
        const entry: GuestBookmarkItem = {...item, addedAt: Date.now()};
        store.items.unshift(entry);
        if (store.items.length > MAX_ITEMS) {
            store.items.length = MAX_ITEMS;
        }
        write(store);
        return store.items;
    }

    static remove(id: string): GuestBookmarkItem[] {
        const store = read();
        const next = store.items.filter((i) => i.id !== id);
        if (next.length === store.items.length) return store.items;
        store.items = next;
        write(store);
        return store.items;
    }

    static clear(): void {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.removeItem(STORAGE_KEY);
            window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
        } catch {
            // best effort
        }
    }

    /**
     * Subscribe to changes (including cross-tab `storage` events).
     * Returns an unsubscribe function.
     */
    static subscribe(listener: () => void): () => void {
        if (typeof window === 'undefined') return () => undefined;
        const storageHandler = (e: StorageEvent) => {
            if (e.key === null || e.key === STORAGE_KEY) listener();
        };
        const customHandler = () => listener();
        window.addEventListener('storage', storageHandler);
        window.addEventListener(CHANGE_EVENT, customHandler);
        return () => {
            window.removeEventListener('storage', storageHandler);
            window.removeEventListener(CHANGE_EVENT, customHandler);
        };
    }
}
